import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { Shield, Users, RefreshCw, Save, AlertTriangle } from 'lucide-react';
import { permissionService } from '../services/api';

const ROLE_LABELS = {
  admin: { label: 'Administrateur', color: 'bg-purple-100 text-purple-700' },
  referent: { label: 'Référent', color: 'bg-blue-100 text-blue-700' },
  animateur: { label: 'Intervenant', color: 'bg-emerald-100 text-emerald-700' },
  financeur: { label: 'Financeur', color: 'bg-amber-100 text-amber-700' },
  'bénéficiaire': { label: 'Bénéficiaire', color: 'bg-slate-100 text-slate-700' }
};

// Les catégories et les clés de permissions viennent du backend
// (GET /permissions/available). Ne jamais les redéclarer ici : une clé qui
// n'existe pas côté serveur produit un interrupteur sans aucun effet.
const CATEGORIE_PAR_DEFAUT = 'Autres';

const PermissionsManager = () => {
  const [availablePermissions, setAvailablePermissions] = useState({});
  const [categories, setCategories] = useState({});
  const [rolePermissions, setRolePermissions] = useState({});
  const [selectedRole, setSelectedRole] = useState('referent');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalPermissions, setOriginalPermissions] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [availableRes, rolesRes] = await Promise.all([
        permissionService.getAvailable(),
        permissionService.getAllRoles()
      ]);
      const permissions = availableRes.data.permissions || {};
      const categoriesServeur = availableRes.data.categories || {};

      // On ne garde que les permissions réellement connues du serveur, et on
      // rattrape celles qui ne seraient dans aucune catégorie.
      const classees = new Set(Object.values(categoriesServeur).flat());
      const nonClassees = Object.keys(permissions).filter((k) => !classees.has(k));
      const categoriesCompletes = { ...categoriesServeur };
      if (nonClassees.length > 0) {
        categoriesCompletes[CATEGORIE_PAR_DEFAUT] = [
          ...(categoriesCompletes[CATEGORIE_PAR_DEFAUT] || []),
          ...nonClassees
        ];
      }
      Object.keys(categoriesCompletes).forEach((cat) => {
        categoriesCompletes[cat] = categoriesCompletes[cat].filter((k) => k in permissions);
        if (categoriesCompletes[cat].length === 0) delete categoriesCompletes[cat];
      });

      setAvailablePermissions(permissions);
      setCategories(categoriesCompletes);
      setRolePermissions(rolesRes.data);
      setOriginalPermissions(JSON.parse(JSON.stringify(rolesRes.data)));
      setHasChanges(false);
    } catch (error) {
      toast.error('Erreur de chargement des permissions');
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionChange = (permission, value) => {
    setRolePermissions(prev => ({
      ...prev,
      [selectedRole]: {
        ...prev[selectedRole],
        [permission]: value
      }
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await permissionService.updateRolePermissions(selectedRole, rolePermissions[selectedRole]);
      
      // Stocker le timestamp de modification des permissions
      localStorage.setItem('permissions_updated_at', Date.now().toString());
      
      toast.success(`Permissions du rôle "${ROLE_LABELS[selectedRole]?.label}" mises à jour`);
      toast.info('Les utilisateurs de ce rôle verront les changements au prochain chargement de page', {
        duration: 5000
      });
      
      setOriginalPermissions(prev => ({
        ...prev,
        [selectedRole]: { ...rolePermissions[selectedRole] }
      }));
      setHasChanges(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm(`Réinitialiser les permissions du rôle "${ROLE_LABELS[selectedRole]?.label}" aux valeurs par défaut ?`)) return;
    
    setSaving(true);
    try {
      const response = await permissionService.resetRolePermissions(selectedRole);
      setRolePermissions(prev => ({
        ...prev,
        [selectedRole]: response.data.permissions
      }));
      setOriginalPermissions(prev => ({
        ...prev,
        [selectedRole]: response.data.permissions
      }));
      setHasChanges(false);
      toast.success('Permissions réinitialisées');
    } catch (error) {
      toast.error('Erreur de réinitialisation');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setRolePermissions(prev => ({
      ...prev,
      [selectedRole]: { ...originalPermissions[selectedRole] }
    }));
    setHasChanges(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-slate-500">Chargement des permissions...</div>
      </div>
    );
  }

  return (
    <Card className="bg-white border-slate-200 rounded-3xl shadow-sm">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-blue-600" />
            <CardTitle className="text-xl font-bold" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Gestion des Permissions par Rôle
            </CardTitle>
          </div>
          {hasChanges && (
            <Badge className="bg-amber-100 text-amber-700 animate-pulse">
              <AlertTriangle className="w-3 h-3 mr-1" />
              Modifications non sauvegardées
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <Tabs value={selectedRole} onValueChange={(val) => {
          if (hasChanges) {
            if (!window.confirm('Des modifications non sauvegardées seront perdues. Continuer ?')) return;
            handleCancel();
          }
          setSelectedRole(val);
        }}>
          <TabsList className="bg-slate-100 rounded-2xl p-1 mb-6 flex-wrap h-auto">
            {Object.entries(ROLE_LABELS).map(([role, info]) => (
              <TabsTrigger 
                key={role} 
                value={role} 
                className="rounded-xl px-4 py-2"
                data-testid={`permission-tab-${role}`}
              >
                <Users className="w-4 h-4 mr-2" />
                {info.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {Object.keys(ROLE_LABELS).map((role) => (
            <TabsContent key={role} value={role}>
              <div className="space-y-6">
                <div className="flex items-center justify-between bg-slate-50 p-4 rounded-2xl">
                  <div>
                    <h3 className="font-bold text-slate-900">
                      Permissions pour le rôle : {' '}
                      <Badge className={ROLE_LABELS[role]?.color}>
                        {ROLE_LABELS[role]?.label}
                      </Badge>
                    </h3>
                    <p className="text-sm text-slate-500 mt-1">
                      Activez ou désactivez les permissions pour ce rôle
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleReset}
                      disabled={saving}
                      className="rounded-xl"
                      data-testid="reset-permissions-btn"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Réinitialiser
                    </Button>
                  </div>
                </div>

                {Object.entries(categories).map(([category, permissions]) => (
                  <div key={category} className="space-y-3">
                    <h4 className="font-bold text-slate-700 text-sm uppercase tracking-wide">
                      {category}
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {permissions.map((perm) => {
                        const isEnabled = rolePermissions[role]?.[perm] ?? false;
                        const isLocked = role === 'admin' && perm === 'manage_permissions';
                        
                        return (
                          <div
                            key={perm}
                            className={`flex items-center justify-between p-3 rounded-xl border ${
                              isEnabled 
                                ? 'bg-emerald-50 border-emerald-200' 
                                : 'bg-slate-50 border-slate-200'
                            } ${isLocked ? 'opacity-60' : ''}`}
                          >
                            <div className="flex-1">
                              <div className="font-medium text-slate-900 text-sm">
                                {availablePermissions[perm] || perm}
                              </div>
                              <div className="text-xs text-slate-500 font-mono">
                                {perm}
                              </div>
                            </div>
                            <Switch
                              checked={isEnabled}
                              onCheckedChange={(checked) => handlePermissionChange(perm, checked)}
                              disabled={isLocked || saving}
                              data-testid={`permission-switch-${perm}`}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}

                {hasChanges && (
                  <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                    <Button
                      variant="outline"
                      onClick={handleCancel}
                      disabled={saving}
                      className="rounded-xl"
                    >
                      Annuler
                    </Button>
                    <Button
                      onClick={handleSave}
                      disabled={saving}
                      className="rounded-xl bg-blue-600 hover:bg-blue-700"
                      data-testid="save-permissions-btn"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
                    </Button>
                  </div>
                )}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default PermissionsManager;
